/**
 * @typedef {Object} UserAccessLevel
 * @property {boolean} free - Whether user is on free plan
 * @property {boolean} pro - Whether user is on pro plan
 * @property {boolean} standard - Whether user is on standard plan
 * @property {boolean} newsletter_paid - Whether user has paid newsletter
 * @property {boolean} newsletter_free - Whether user has free newsletter
 */

/**
 * @typedef {Object} UserCommerce
 * @property {Object} [additionalProperties] - Commerce-related properties
 */

/**
 * @typedef {Object} KitUser
 * @property {number} id - User ID
 * @property {number} account_id - Account ID
 * @property {string} email - User's email address
 * @property {string|null} ip_country - User's IP country
 * @property {boolean} anonymous - Whether user is anonymous
 * @property {boolean} admin - Whether user is an admin
 * @property {boolean} pro - Whether user is pro
 * @property {boolean} blocklisted - Whether user is blocklisted
 * @property {boolean} account_is_multi_user - Whether account has multiple users
 * @property {UserAccessLevel} access_level - User's access level details
 * @property {boolean} onTrial - Whether user is on trial
 * @property {string[]} allowed_activities - List of allowed activities/permissions
 * @property {number} allowed_sequences_count - Number of allowed sequences (-1 for unlimited)
 * @property {number} allowed_automations_count - Number of allowed automations (-1 for unlimited)
 * @property {string} timezone - User's timezone
 * @property {boolean} account_in_control_group - Whether account is in control group
 * @property {boolean} confirmed - Whether user is confirmed
 * @property {UserCommerce} commerce - Commerce-related settings
 * @property {string[]} colors - User's color preferences
 * @property {string} account_name - User's account name
 * @property {Date} account_created_at - Account creation date
 */

import React, { useState, useEffect, useMemo } from "react"
import { useFetch } from "hooks/use-new-fetch"

export const UserContext = React.createContext({})

/**
 * Provider component that makes user data available to all child components
 * @param {Object} props
 * @param {React.ReactNode} props.children - Child components
 * @returns {JSX.Element} UserContext.Provider component
 */
export function UserProvider({ children }) {
  // Currently, the user information is provided directly on the window object.
  // This utility is written using Context and hooks to future proof it for changes
  // where the user is provided in other ways or can change without the page
  // refreshing.
  const initialUser = (window.app && window.app.user) || {}
  const [user, setUser] = useState(initialUser)
  const [refreshAllowedActivitiesRequest] = useFetch("/user/allowed_activities")

  const refreshAllowedActivities = async () => {
    const { data } = await refreshAllowedActivitiesRequest.get()
    setUser(prevUser => ({ ...prevUser, allowed_activities: data }))
  }

  useEffect(() => {
    window.user = user
  }, [user])

  return (
    <UserContext.Provider value={{ ...user, refreshAllowedActivities }}>
      {children}
    </UserContext.Provider>
  )
}

export function useCurrentUser() {
  const user = React.useContext(UserContext)

  if (!user) {
    console.warn(
      "Information about the current user is not set on `window.app`, or the UserProvider component is not used in this tree."
    )
  }

  const allowedActivities = user?.allowed_activities || []

  const canAddSequence = allowedActivities.includes("sequences.create")
  const canAddSubscribersWithoutCreatorNetwork = allowedActivities.includes(
    "subscribers.add_without_creator_network"
  )
  const canAddVA = allowedActivities.includes("va.create")
  const canCancelAccount = allowedActivities.includes("account.cancel")
  const canComplianceModify = allowedActivities.includes("compliance_modify")
  const canDisableRecommendations = allowedActivities.includes(
    "disable_recommendations"
  )
  const canUsePaidRecommendations = allowedActivities.includes(
    "paid_recommendations"
  )
  const canRemoveCkBranding = allowedActivities.includes("remove_ck_branding")
  const canUseAPI = allowedActivities.includes("api")
  const canUseTemplateLibrary = allowedActivities.includes(
    "va.template_library"
  )
  const canActivateSequence = allowedActivities.includes("sequences.activate")
  // TODO: check accountCanAccessApps when free apps project is complete
  const accountCanAccessApps = allowedActivities.includes("apps")
  const canUsePolls = allowedActivities.includes("polls")
  const canUseRules = allowedActivities.includes("rules")
  const canUseVAs = allowedActivities.includes("va")
  const canUseSequences = allowedActivities.includes("sequences")
  const canAddCustomFavicon = allowedActivities.includes("custom_favicon")
  const canUploadCustomFonts = allowedActivities.includes("custom_fonts")
  const canViewAccountSettings = allowedActivities.includes(
    "account_settings.read"
  )
  const canUseVersionHistory = allowedActivities.includes("version_history")
  const canUseWebhooks = allowedActivities.includes("webhooks_v2")
  const canViewSubscriberKitFields =
    allowedActivities.includes("subscriber_signals")

  return useMemo(
    () => ({
      ...user,
      canActivateSequence,
      canAddSequence,
      canAddSubscribersWithoutCreatorNetwork,
      canAddVA,
      canCancelAccount,
      canComplianceModify,
      canDisableRecommendations,
      canRemoveCkBranding,
      canUseAPI,
      canUsePaidRecommendations,
      canUsePolls,
      accountCanAccessApps, // TODO: check when free apps project is complete
      canUseTemplateLibrary,
      canUseRules,
      canUseVAs,
      canUseSequences,
      canAddCustomFavicon,
      canUploadCustomFonts,
      canViewAccountSettings,
      canUseVersionHistory,
      canUseWebhooks,
      canViewSubscriberKitFields,
      signalsAccessLevel: user?.signals_access_level ?? {
        state: null,
        enabled: false
      }
    }),
    [
      user,
      canActivateSequence,
      canAddSequence,
      canAddSubscribersWithoutCreatorNetwork,
      canAddVA,
      canCancelAccount,
      canComplianceModify,
      canDisableRecommendations,
      canRemoveCkBranding,
      canUseAPI,
      canUsePaidRecommendations,
      canUsePolls,
      accountCanAccessApps, // TODO: check when free apps project is complete
      canUseTemplateLibrary,
      canUseRules,
      canUseVAs,
      canUseSequences,
      canAddCustomFavicon,
      canUploadCustomFonts,
      canViewAccountSettings,
      canUseVersionHistory,
      canUseWebhooks,
      canViewSubscriberKitFields
    ]
  )
}
